/**
 * Create-issue modal — POSTs to /api/v1/issues, navigates to the new issue on success.
 *
 * Project is locked when the user opens the modal from a project-specific board;
 * a project picker appears when opened from the all-boards view.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { useDirectory } from "@/hooks/useDirectory";
import { api } from "@/lib/api";
import type { Issue, Project } from "@/lib/types";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Pre-selected project key. If undefined, modal shows a project picker. */
  defaultProjectKey?: string;
};

// The backend still accepts the legacy types, but the product hierarchy is
// two levels — Epic → Task — so the create form only offers those two.
type IssueType = "task" | "epic";
type IssuePriority = "P1" | "P2" | "P3";

const TYPES: IssueType[] = ["task", "epic"];
const TYPE_LABEL: Record<IssueType, string> = { task: "Task", epic: "Epic" };
const PRIORITIES: IssuePriority[] = ["P1", "P2", "P3"];

export function CreateIssueModal({ open, onClose, defaultProjectKey }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get<Project[]>("/projects")).data,
    enabled: open,
  });
  // Name-only roster so the assignee picker works for employees too.
  const { members } = useDirectory();

  const [projectKey, setProjectKey] = useState<string>(defaultProjectKey ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [issueType, setIssueType] = useState<IssueType>("task");
  const [priority, setPriority] = useState<IssuePriority>("P2");
  const [estimate, setEstimate] = useState<string>("");
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [labels, setLabels] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // When the modal opens fresh, lock the project to the board it was opened from.
  // If none provided (all-boards view), pre-select the first project.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTitle("");
    setDescription("");
    setIssueType("task");
    setPriority("P2");
    setEstimate("");
    setAssigneeId("");
    setLabels("");
    if (defaultProjectKey) {
      setProjectKey(defaultProjectKey);
    } else if (projects.length > 0) {
      setProjectKey(projects[0].key);
    }
  }, [open, defaultProjectKey, projects]);

  const submit = useMutation({
    mutationFn: async () => {
      const body = {
        project_key: projectKey,
        title: title.trim(),
        description_md: description.trim() || null,
        issue_type: issueType,
        priority,
        estimate_points: estimate.trim() === "" ? null : Number(estimate),
        assignee_id: assigneeId || null,
        labels: labels
          .split(",")
          .map((l) => l.trim())
          .filter(Boolean),
      };
      return (await api.post<Issue>("/issues", body)).data;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["issues"] });
      onClose();
      navigate(`/issues/${created.key}`);
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } }).response?.data
          ?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? (detail[0] as { msg?: string })?.msg ?? "Could not create task."
            : "Could not create task.",
      );
    },
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="create-issue-title"
      onClick={onClose}
      style={overlay}
    >
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <h2 id="create-issue-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 16px" }}>
          Create task
        </h2>

        {!defaultProjectKey && (
          <label style={fieldRow}>
            <span style={fieldLabel}>Project</span>
            <select
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value)}
              style={input}
            >
              {projects.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name} · {p.key}
                </option>
              ))}
            </select>
          </label>
        )}

        <label style={fieldRow}>
          <span style={fieldLabel}>Title</span>
          <input
            autoFocus
            type="text"
            placeholder="Briefly describe the task"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={input}
            maxLength={512}
          />
        </label>

        <label style={fieldRow}>
          <span style={fieldLabel}>Description</span>
          <textarea
            placeholder="Markdown is rendered on the detail page."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            style={{ ...input, resize: "vertical", fontFamily: "var(--font-sans)" }}
          />
        </label>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
          }}
        >
          <label>
            <span style={fieldLabel}>Type</span>
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value as IssueType)}
              style={input}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={fieldLabel}>Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as IssuePriority)}
              style={input}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={fieldLabel}>Estimate (pts)</span>
            <input
              type="number"
              min={0}
              max={100}
              placeholder="—"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              style={input}
            />
          </label>
        </div>

        <label style={fieldRow}>
          <span style={fieldLabel}>Assignee</span>
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            style={input}
          >
            <option value="">Unassigned</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.display_name}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldRow}>
          <span style={fieldLabel}>Labels (comma-separated)</span>
          <input
            type="text"
            placeholder="growth, polish, a11y"
            value={labels}
            onChange={(e) => setLabels(e.target.value)}
            style={input}
          />
        </label>

        {error && (
          <p
            role="alert"
            style={{
              padding: "8px 12px",
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              marginTop: 12,
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!projectKey) {
                setError("Pick a project.");
                return;
              }
              if (!title.trim()) {
                setError("Title is required.");
                return;
              }
              submit.mutate();
            }}
            disabled={submit.isPending}
            style={{
              ...btnPrimary,
              cursor: submit.isPending ? "wait" : "pointer",
              opacity: submit.isPending ? 0.6 : 1,
            }}
          >
            {submit.isPending ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  backdropFilter: "blur(4px)",
  display: "grid",
  placeItems: "center",
  zIndex: 100,
};

const card: CSSProperties = {
  width: "min(520px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow:
    "0 30px 18px rgba(0,0,0,0.06), 0 13px 13px rgba(0,0,0,0.10), 0 3px 7px rgba(0,0,0,0.12)",
  maxHeight: "calc(100vh - 64px)",
  overflowY: "auto",
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
};

const btnSecondary: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
};
